import type { DynamicCard as CustomElement } from "@nosto/web-components"
import { JSX } from "preact"
import { useEffect, useRef } from "preact/hooks"
import { Props } from "@/types"

type DynamicCardProps = Props<CustomElement> &
  JSX.IntrinsicElements["span"] & {
    preferredColor?: string
    showOnlyClearanceSwatches?: boolean
    orderRegularSwatchesFirst?: boolean
  }

const DYNAMIC_CARD_LOADED_EVENT = "@nosto/DynamicCard/loaded"
const CLEARANCE_FILTER_STATE_ATTR = "data-clearance-filter-state"
const SWATCH_NORMALIZATION_STATE_ATTR = "data-swatch-normalization-state"
const CARD_READINESS_STATE_ATTR = "data-card-readiness-state"
const PRODUCT_IMAGE_STATE_ATTR = "data-product-image-state"
const CARD_READINESS_TIMEOUT_MS = 5000
const NON_SELECTABLE_SWATCH_ATTRS = ["data-disabled", "data-unavailable", "data-sold-out", "data-coming-soon"]

function isSelectableSwatch(swatch: HTMLElement) {
  return !swatch.hidden && NON_SELECTABLE_SWATCH_ATTRS.every(attr => !swatch.hasAttribute(attr))
}

function hasVariantChangeMetadata(swatch: HTMLElement) {
  return Boolean(swatch.dataset.img && swatch.dataset.url)
}

function selectSwatch(card: HTMLElement, swatch: HTMLElement) {
  const swatchRoot = swatch.closest("product-swatches") ?? card
  const swatches = Array.from(swatchRoot.querySelectorAll<HTMLElement>("[data-swatch]"))

  swatches.forEach(candidate => {
    const isSelected = candidate === swatch
    candidate.toggleAttribute("data-active", isSelected)
    candidate.setAttribute("aria-selected", isSelected ? "true" : "false")
    candidate.setAttribute("aria-checked", isSelected ? "true" : "false")
    candidate.setAttribute("tabindex", isSelected ? "0" : "-1")

    const radio = candidate.querySelector<HTMLInputElement>('input[type="radio"]')
    if (radio) {
      radio.checked = isSelected
    }
  })
}

function dispatchVariantChange(card: HTMLElement, swatch: HTMLElement) {
  const productCard = card.querySelector("product-card")
  productCard?.dispatchEvent(
    new CustomEvent("variant-change", {
      detail: {
        imageUrl: swatch.dataset.img,
        productUrl: swatch.dataset.url,
        price: swatch.dataset.price,
        compareAtPrice: swatch.dataset.compare
      }
    })
  )
}

function applyPreferredColor(card: HTMLElement, preferredColor: string) {
  const normalizedColor = preferredColor.toLowerCase()
  const swatches = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]"))
  const swatch = swatches.find(s => {
    const label = s.getAttribute("data-option-value") || s.querySelector("img")?.getAttribute("alt") || ""
    return label.toLowerCase().includes(normalizedColor) && isSelectableSwatch(s) && hasVariantChangeMetadata(s)
  })

  if (!swatch) return

  selectSwatch(card, swatch)
  dispatchVariantChange(card, swatch)
}

function showOnlyClearanceSwatches(card: HTMLElement) {
  const swatchLists = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch-list]"))

  for (const list of swatchLists) {
    const swatches = Array.from(list.querySelectorAll<HTMLElement>("[data-swatch]"))
    const hasClearanceMetadata = swatches.some(swatch => swatch.dataset.clearance !== undefined)
    if (!hasClearanceMetadata) continue

    swatches.forEach(swatch => {
      swatch.hidden = swatch.dataset.clearance !== "true"
    })
  }
}

function orderRegularSwatchesFirst(card: HTMLElement) {
  const swatchLists = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch-list]"))

  for (const list of swatchLists) {
    const swatches = Array.from(list.querySelectorAll<HTMLElement>("[data-swatch]"))
    const hasClearanceMetadata = swatches.some(swatch => swatch.dataset.clearance !== undefined)
    if (!hasClearanceMetadata) continue

    swatches.forEach(swatch => {
      swatch.hidden = false
    })
    const regularSwatches = swatches.filter(swatch => swatch.dataset.clearance !== "true")
    const clearanceSwatches = swatches.filter(swatch => swatch.dataset.clearance === "true")
    regularSwatches.concat(clearanceSwatches).forEach(swatch => list.appendChild(swatch))
  }
}

function hasClearanceMetadata(card: HTMLElement) {
  return Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]")).some(
    swatch => swatch.dataset.clearance !== undefined
  )
}

async function reloadCardFromCurrentOrigin(
  card: HTMLElement,
  props: Pick<DynamicCardProps, "handle" | "template" | "variantId">
) {
  if (!props.handle || !props.template) return false

  const url = new URL(`/products/${props.handle}`, globalThis.location.origin)
  url.searchParams.set("view", props.template)
  url.searchParams.set("layout", "none")
  if (props.variantId) {
    url.searchParams.set("variant", props.variantId)
  }

  const response = await fetch(url.href, {
    cache: "no-store",
    credentials: "same-origin"
  })
  if (!response.ok) return false

  const html = await response.text()
  if (/<(body|html)/i.test(html)) return false

  card.innerHTML = html
  return true
}

function ensureActiveSwatch(card: HTMLElement) {
  const activeSwatch = card.querySelector<HTMLElement>("[data-swatch][data-active]")
  const firstUsableSwatch = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]")).find(
    s => isSelectableSwatch(s) && hasVariantChangeMetadata(s)
  )
  const swatch =
    activeSwatch && isSelectableSwatch(activeSwatch) && hasVariantChangeMetadata(activeSwatch)
      ? activeSwatch
      : firstUsableSwatch

  if (swatch) {
    selectSwatch(card, swatch)
    dispatchVariantChange(card, swatch)
  }
}

function setReadinessState(card: HTMLElement, state: "pending" | "ready") {
  if (card.getAttribute(CARD_READINESS_STATE_ATTR) !== state) {
    card.setAttribute(CARD_READINESS_STATE_ATTR, state)
  }
}

function hasRenderedPrice(productCard: HTMLElement) {
  const priceWrapper = productCard.querySelector<HTMLElement>("[data-price-wrapper]")
  if (!priceWrapper) return true

  const priceComponent = priceWrapper.querySelector<HTMLElement>("price-component")
  if (priceComponent?.getAttribute("price")) return true

  return Boolean(priceWrapper.textContent?.trim())
}

function hasReadyProductImage(productCard: HTMLElement) {
  const image = productCard.querySelector<HTMLImageElement>("[data-product-image]")
  if (!image) return true

  const imageWrapper = image.closest<HTMLElement>(".product-card__image-wrapper")
  if (imageWrapper?.getAttribute(PRODUCT_IMAGE_STATE_ATTR) === "ready") return true

  return image.complete && image.naturalWidth > 0
}

function hasReadyActiveSwatch(card: HTMLElement) {
  const swatches = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]"))
  if (swatches.length === 0) return true

  return swatches.some(swatch => swatch.hasAttribute("data-active") && isSelectableSwatch(swatch))
}

function isCardReady(card: HTMLElement, requireActiveSwatch: boolean) {
  const productCard = card.querySelector<HTMLElement>("product-card")
  if (!productCard) return false
  if (!hasRenderedPrice(productCard)) return false
  if (card.getAttribute(SWATCH_NORMALIZATION_STATE_ATTR) === "pending") return false
  if (requireActiveSwatch && !hasReadyActiveSwatch(card)) return false
  return hasReadyProductImage(productCard)
}

function DynamicCardSkeleton() {
  return (
    <div className="nosto-dynamic-card-skeleton" aria-hidden="true">
      <div className="nosto-dynamic-card-skeleton__image" />
      <div className="nosto-dynamic-card-skeleton__swatches">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="nosto-dynamic-card-skeleton__line" />
      <div className="nosto-dynamic-card-skeleton__line nosto-dynamic-card-skeleton__line--short" />
    </div>
  )
}

/**
 * A custom element wrapper that renders a product by fetching the markup from Shopify based on the provided handle and template.
 *
 * This component is designed to be used in a Shopify environment and fetches product data dynamically.
 */
export default function DynamicCard({
  preferredColor,
  showOnlyClearanceSwatches: onlyClearanceSwatches,
  orderRegularSwatchesFirst: regularSwatchesFirst,
  children,
  ...props
}: DynamicCardProps) {
  const cardRef = useRef<HTMLElement>(null)
  const { handle, template, variantId } = props

  useEffect(() => {
    const card = cardRef.current
    if (!card) return
    let didReloadFromCurrentOrigin = false
    let didReachReadinessFallback = false
    let watchedImage: HTMLImageElement | null = null
    const requiresSwatchNormalization = Boolean(onlyClearanceSwatches || regularSwatchesFirst)
    const requireActiveSwatch = Boolean(onlyClearanceSwatches)
    let readinessFallbackTimer: number | undefined = window.setTimeout(() => {
      didReachReadinessFallback = true
      syncCardReadiness()
    }, CARD_READINESS_TIMEOUT_MS)

    const clearReadinessFallback = () => {
      if (readinessFallbackTimer === undefined) return
      window.clearTimeout(readinessFallbackTimer)
      readinessFallbackTimer = undefined
    }

    const syncCardReadiness = () => {
      const isReady = didReachReadinessFallback || isCardReady(card, requireActiveSwatch)
      setReadinessState(card, isReady ? "ready" : "pending")
      if (isReady) {
        clearReadinessFallback()
      }

      const image = card.querySelector<HTMLImageElement>("[data-product-image]")
      const productCard = image?.closest<HTMLElement>("product-card")
      if (image && image !== watchedImage && productCard && !hasReadyProductImage(productCard)) {
        watchedImage = image
        image.addEventListener("load", syncCardReadiness, { once: true })
        image.addEventListener("error", syncCardReadiness, { once: true })
      }
    }

    setReadinessState(card, "pending")
    if (onlyClearanceSwatches || regularSwatchesFirst) {
      card.setAttribute(SWATCH_NORMALIZATION_STATE_ATTR, "pending")
      if (onlyClearanceSwatches) {
        card.setAttribute(CLEARANCE_FILTER_STATE_ATTR, "pending")
      }
    }

    const readinessObserver = new MutationObserver(syncCardReadiness)
    readinessObserver.observe(card, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true
    })

    const updateCard = async () => {
      const hasLoadedMarkup = Boolean(card.querySelector("product-card"))
      if (requiresSwatchNormalization && !hasLoadedMarkup) {
        syncCardReadiness()
        return
      }

      if (onlyClearanceSwatches && hasLoadedMarkup && !hasClearanceMetadata(card)) {
        card.setAttribute(SWATCH_NORMALIZATION_STATE_ATTR, "pending")
        if (onlyClearanceSwatches) {
          card.setAttribute(CLEARANCE_FILTER_STATE_ATTR, "pending")
        }
        if (didReloadFromCurrentOrigin) return
        didReloadFromCurrentOrigin = true
        let didReloadSuccessfully = false
        try {
          didReloadSuccessfully = await reloadCardFromCurrentOrigin(card, { handle, template, variantId })
        } catch {
          didReloadSuccessfully = false
        }
        if (!didReloadSuccessfully || !hasClearanceMetadata(card)) return
      }
      if (onlyClearanceSwatches) {
        showOnlyClearanceSwatches(card)
      } else if (regularSwatchesFirst) {
        orderRegularSwatchesFirst(card)
      }
      if (preferredColor) {
        applyPreferredColor(card, preferredColor)
      }
      if (onlyClearanceSwatches) {
        ensureActiveSwatch(card)
        card.setAttribute(CLEARANCE_FILTER_STATE_ATTR, "ready")
      }
      if (onlyClearanceSwatches || regularSwatchesFirst) {
        card.setAttribute(SWATCH_NORMALIZATION_STATE_ATTR, "ready")
      }
      syncCardReadiness()
    }
    card.addEventListener(DYNAMIC_CARD_LOADED_EVENT, updateCard)
    updateCard()
    syncCardReadiness()

    return () => {
      clearReadinessFallback()
      readinessObserver.disconnect()
      card.removeEventListener(DYNAMIC_CARD_LOADED_EVENT, updateCard)
      card.removeAttribute(CLEARANCE_FILTER_STATE_ATTR)
      card.removeAttribute(SWATCH_NORMALIZATION_STATE_ATTR)
      card.removeAttribute(CARD_READINESS_STATE_ATTR)
    }
  }, [preferredColor, onlyClearanceSwatches, regularSwatchesFirst, handle, template, variantId])

  return (
    <nosto-dynamic-card
      ref={cardRef}
      data-clearance-filter-state={onlyClearanceSwatches ? "pending" : undefined}
      data-swatch-normalization-state={onlyClearanceSwatches || regularSwatchesFirst ? "pending" : undefined}
      data-card-readiness-state="pending"
      {...props}
    >
      {children ?? <DynamicCardSkeleton />}
    </nosto-dynamic-card>
  )
}

declare module "preact/jsx-runtime" {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "nosto-dynamic-card": DynamicCardProps
    }
  }
}
